import { getPostHogRuntimeConfig } from './posthogConfig'

const INTERNAL_ANALYTICS_STORAGE_KEY = 'parnasa_internal_analytics'
const INTERNAL_ANALYTICS_PARAM = 'pm_internal'
const INTERNAL_ANALYTICS_PROPERTIES = {
  internal_user: true,
  $internal_or_test_user: true,
}

let posthogClientPromise = null
let hasForcedSessionRecording = false

function getBrowserInternalAnalyticsState() {
  if (typeof window === 'undefined') return false

  try {
    const url = new URL(window.location.href)
    const paramValue = url.searchParams.get(INTERNAL_ANALYTICS_PARAM)

    if (paramValue === '1' || paramValue === 'true') {
      window.localStorage?.setItem(INTERNAL_ANALYTICS_STORAGE_KEY, 'true')
      url.searchParams.delete(INTERNAL_ANALYTICS_PARAM)
      window.history?.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
      return true
    }

    if (paramValue === '0' || paramValue === 'false') {
      window.localStorage?.removeItem(INTERNAL_ANALYTICS_STORAGE_KEY)
      url.searchParams.delete(INTERNAL_ANALYTICS_PARAM)
      window.history?.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
      return false
    }

    return window.localStorage?.getItem(INTERNAL_ANALYTICS_STORAGE_KEY) === 'true'
  } catch (error) {
    console.error('Unable to read internal analytics marker:', error)
    return false
  }
}

function maybeRegisterInternalAnalytics(posthog) {
  if (!posthog || typeof posthog.register !== 'function') return
  if (!getBrowserInternalAnalyticsState()) return

  try {
    posthog.register(INTERNAL_ANALYTICS_PROPERTIES)
  } catch (error) {
    console.error('Unable to register internal analytics marker:', error)
  }
}

function isScriptError(event) {
  return (
    event?.event === '$exception' &&
    typeof event?.properties?.$exception_message === 'string' &&
    event.properties.$exception_message.trim().toLowerCase() === 'script error.'
  )
}

function getExceptionMessages(event) {
  const properties = event?.properties || {}
  const messages = [
    properties.$exception_message,
    ...(Array.isArray(properties.$exception_values) ? properties.$exception_values : []),
  ]

  if (Array.isArray(properties.$exception_list)) {
    for (const exception of properties.$exception_list) {
      messages.push(exception?.value)
    }
  }

  return messages
    .filter((message) => typeof message === 'string')
    .map((message) => message.trim())
    .filter(Boolean)
}

function isKnownNextFrameworkBrowserNoise(event) {
  if (event?.event !== '$exception') return false

  const messages = getExceptionMessages(event)
  return messages.some((message) => (
    message === 'NEXT_REDIRECT' ||
    message === 'Connection closed.' ||
    message.includes('Minified React error #418') ||
    message.includes('Minified React error #419')
  ))
}

function markInternalAnalyticsEvent(event) {
  if (!event) return event
  if (isScriptError(event)) return null
  if (isKnownNextFrameworkBrowserNoise(event)) return null

  if (!getBrowserInternalAnalyticsState()) return event

  return {
    ...event,
    properties: {
      ...(event?.properties || {}),
      ...INTERNAL_ANALYTICS_PROPERTIES,
    },
  }
}

function maybeForceSessionRecording(posthog, options = {}) {
  if (!options.forceSessionRecording || hasForcedSessionRecording || !posthog) return
  if (typeof posthog.startSessionRecording !== 'function') return

  try {
    posthog.startSessionRecording(true)
    hasForcedSessionRecording = true
  } catch (error) {
    console.error('Unable to force PostHog session recording:', error)
  }
}

export function initializePostHogClient(options = {}) {
  if (typeof window === 'undefined') {
    return Promise.resolve(null)
  }

  const config = getPostHogRuntimeConfig()
  if (!config.enabled) {
    return Promise.resolve(null)
  }

  if (!posthogClientPromise) {
    posthogClientPromise = import('posthog-js')
      .then(({ default: posthog }) => {
        if (!posthog.__loaded) {
          posthog.init(config.key, {
            api_host: config.host,
            ui_host: config.uiHost,
            defaults: '2026-01-30',
            capture_pageview: true,
            capture_exceptions: true,
            session_recording: {
              maskAllInputs: false,
            },
            before_send: markInternalAnalyticsEvent,
            debug: false,
          })
        }

        return posthog
      })
      .catch((error) => {
        console.error('Unable to initialize PostHog client:', error)
        posthogClientPromise = Promise.resolve(null)
        return null
      })
  }

  return posthogClientPromise.then((posthog) => {
    maybeRegisterInternalAnalytics(posthog)
    maybeForceSessionRecording(posthog, options)
    return posthog
  })
}

export function capturePostHogEvent(eventName, properties = {}) {
  if (typeof window === 'undefined' || !eventName) return

  void initializePostHogClient().then((posthog) => {
    if (!posthog) return

    try {
      posthog.capture(eventName, properties)
    } catch (error) {
      console.error(`Unable to capture PostHog event "${eventName}":`, error)
    }
  })
}

export async function identifyPostHogUser(userId, properties = {}) {
  if (typeof window === 'undefined' || !userId) return null

  const posthog = await initializePostHogClient()
  if (!posthog || typeof posthog.identify !== 'function') return null

  try {
    posthog.identify(userId, properties)
    return posthog
  } catch (error) {
    console.error('Unable to identify PostHog user:', error)
    return null
  }
}

export async function getPostHogDistinctId() {
  if (typeof window === 'undefined') return null

  const posthog = await initializePostHogClient()
  if (!posthog || typeof posthog.get_distinct_id !== 'function') return null

  try {
    const distinctId = posthog.get_distinct_id()
    return distinctId ? String(distinctId) : null
  } catch (error) {
    console.error('Unable to read PostHog distinct id:', error)
    return null
  }
}

// Server signup captures need the browser anon id to stitch pre-signup funnels.
export async function attachPostHogDistinctId(formData) {
  if (!(formData instanceof FormData)) return formData

  const distinctId = await getPostHogDistinctId()
  if (distinctId) formData.set('posthog_distinct_id', distinctId)
  return formData
}
